import { DragEvent, useCallback, useMemo, useState } from 'react';
import { GripVertical, Loader2, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { showErrorToast } from '@Helpers';
import { cn } from '@Utils';

import Badge from '@Components/Badge/Badge';
import ConfirmationModal from '@Components/ConfirmationModal/confirmationModal';
import Table from '@Components/Table/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@Components/ui-v2/select';
import { ScheduleTask, TaskType } from '@Models';
import { bopsApi } from '@Network';
import { TABLE_HEADER_CELL_CLASS_NAME, TABLE_SURFACE_CLASS_NAME } from '../../FieldServices/constants';
import { getErrorDetail } from '../constants';
import { getTaskTypeName } from '../Wizard/constants';
import {
  getMayNotFitTaskIds,
  getReviewStatusFilter,
  reorderTasksByDrag,
  REVIEW_STATUS_FILTER_TO_WRITE,
  REVIEW_STATUS_FILTER_VALUES,
  REVIEW_STATUS_FILTER_VARIANT,
  ReviewStatusFilter,
  sortTasksByPriority,
} from './constants';

const REVIEW_STATUS_TRIGGER_CLASS_NAME: Record<ReviewStatusFilter, string> = {
  pending: 'border-gray-300 bg-gray-50 text-gray-600',
  approved: 'border-success-200 bg-success-50 text-success-700',
  rejected: 'border-error-200 bg-error-50 text-error-700',
};

const REVIEW_STATUS_CHIP_SIZE_CLASS_NAME = 'h-7 rounded-[7px] px-2.5 py-0 text-xs font-semibold';

const CELL_CLASS_NAME = 'px-4 py-3 text-xs';
const HEADER_CELL_CLASS_NAME = `px-4 ${TABLE_HEADER_CELL_CLASS_NAME}`;

interface TaskTableProps {
  scheduleId: string;
  weekStart: string | null;
  tasks: ScheduleTask[];
  allTasks: ScheduleTask[];
  taskTypesById: Record<string, TaskType>;
  canManage: boolean;
  roomSlots: number | null;
  isInheritedList: boolean;
  notContractedTaskIds: Set<string>;
  onTasksChanged: () => Promise<void>;
}

const TaskTable = ({
  scheduleId,
  weekStart,
  tasks,
  allTasks,
  taskTypesById,
  canManage,
  roomSlots,
  isInheritedList,
  notContractedTaskIds,
  onTasksChanged,
}: TaskTableProps) => {
  const { t } = useTranslation();
  const [updatingTaskIds, setUpdatingTaskIds] = useState<Set<string>>(new Set());
  const [reorderingTaskId, setReorderingTaskId] = useState<string | null>(null);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dropTargetTaskId, setDropTargetTaskId] = useState<string | null>(null);
  const [removingTaskId, setRemovingTaskId] = useState<string | null>(null);
  const [taskToRemove, setTaskToRemove] = useState<ScheduleTask | null>(null);

  const rankedTasks = useMemo(() => sortTasksByPriority(tasks), [tasks]);
  const sortedTasks = useMemo(() => rankedTasks.map((entry) => entry.task), [rankedTasks]);
  const rankedAllTasks = useMemo(() => sortTasksByPriority(allTasks), [allTasks]);
  const positionById = useMemo(() => {
    const map: Record<string, number> = {};
    rankedAllTasks.forEach((entry, position) => {
      map[entry.task.id] = position;
    });
    return map;
  }, [rankedAllTasks]);
  const mayNotFitTaskIds = useMemo(
    () => getMayNotFitTaskIds(allTasks, roomSlots, isInheritedList, notContractedTaskIds),
    [allTasks, roomSlots, isInheritedList, notContractedTaskIds],
  );

  const reviewStatusOptions = useMemo(
    () =>
      REVIEW_STATUS_FILTER_VALUES.map((value) => ({
        value,
        label: t(`Schedule.Detail.ReviewStatus.${value}`),
      })),
    [t],
  );

  const getTaskTypeLabel = useCallback(
    (taskTypeId: string): string => {
      const taskType = taskTypesById[taskTypeId];
      return taskType ? getTaskTypeName(taskType, t) : taskTypeId;
    },
    [taskTypesById, t],
  );

  const handleReviewStatusChange = async (task: ScheduleTask, next: ReviewStatusFilter) => {
    if (next === getReviewStatusFilter(task.review_status)) return;
    setUpdatingTaskIds((prev) => new Set(prev).add(task.id));
    try {
      await bopsApi.bulkUpdateScheduleTaskReviewStatus([task.id], REVIEW_STATUS_FILTER_TO_WRITE[next], weekStart);
    } catch (error) {
      showErrorToast(getErrorDetail(error) ?? t('Schedule.Detail.ReviewStatusUpdateError'));
    } finally {
      await onTasksChanged();
      setUpdatingTaskIds((prev) => {
        const updatedIds = new Set(prev);
        updatedIds.delete(task.id);
        return updatedIds;
      });
    }
  };

  const handleDrop = async (targetTask: ScheduleTask) => {
    const fromId = draggedTaskId;
    setDraggedTaskId(null);
    setDropTargetTaskId(null);
    if (!fromId || reorderingTaskId) return;

    const reordered = reorderTasksByDrag(allTasks, fromId, targetTask.id);
    if (!reordered) return;

    setReorderingTaskId(fromId);
    try {
      await bopsApi.reorderScheduleTasks(
        scheduleId,
        weekStart,
        reordered.map((task) => task.id),
      );
    } catch (error) {
      showErrorToast(getErrorDetail(error) ?? t('Schedule.Detail.PriorityUpdateError'));
    } finally {
      await onTasksChanged();
      setReorderingTaskId(null);
    }
  };

  const handleRemove = async (task: ScheduleTask) => {
    setRemovingTaskId(task.id);
    try {
      await bopsApi.deleteScheduleTask(task.id, weekStart);
    } catch (error) {
      showErrorToast(getErrorDetail(error) ?? t('Schedule.Detail.RemoveTaskError'));
    } finally {
      await onTasksChanged();
      setRemovingTaskId(null);
    }
  };

  const handleConfirmRemove = async () => {
    if (!taskToRemove) return;
    setTaskToRemove(null);
    await handleRemove(taskToRemove);
  };

  const getRemoveConfirmText = (task: ScheduleTask): string => {
    const taskType = getTaskTypeLabel(task.task_type_id);
    return task.categories.length > 0
      ? t('Schedule.Detail.RemoveTaskConfirmWithCategory', { taskType, category: task.categories.join(', ') })
      : t('Schedule.Detail.RemoveTaskConfirm', { taskType });
  };

  const columns = useMemo(
    () => [
      {
        Header: t('Schedule.Detail.Task.Priority'),
        accessor: 'priority_rank',
        width: 'w-14 pr-0',
        Cell: ({ row }: { row: { original: ScheduleTask } }) => {
          const task = row.original;
          const position = positionById[task.id];

          if (reorderingTaskId === task.id) {
            return <Loader2 size={16} className="animate-spin text-gray-400" />;
          }

          return (
            <div className="flex items-center gap-1">
              {canManage && (
                <span
                  draggable={!reorderingTaskId}
                  onDragStart={(event: DragEvent<HTMLSpanElement>) => {
                    if (reorderingTaskId) return;
                    const { dataTransfer, currentTarget } = event;
                    const rowElement = currentTarget.closest('tr');
                    if (rowElement) {
                      dataTransfer.setDragImage(rowElement, 24, 24);
                    }
                    dataTransfer.setData('text/plain', task.id);
                    dataTransfer.effectAllowed = 'move';
                    setDraggedTaskId(task.id);
                  }}
                  onDragEnd={() => {
                    setDraggedTaskId(null);
                    setDropTargetTaskId(null);
                  }}
                  className={cn(
                    'flex flex-shrink-0 items-center',
                    reorderingTaskId ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing',
                  )}>
                  <GripVertical size={14} className="text-gray-300" aria-hidden="true" />
                </span>
              )}
              <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md border border-gray-150 bg-gray-50 text-xs font-bold tabular-nums text-gray-700">
                {position + 1}
              </span>
            </div>
          );
        },
      },
      {
        Header: t('Schedule.Detail.Task.Area'),
        accessor: 'area',
        width: 'w-28',
        Cell: ({ row }: { row: { original: ScheduleTask } }) => (
          <span className="text-gray-500">{row.original.area || t('Schedule.Detail.Task.NoCategory')}</span>
        ),
      },
      {
        Header: t('Schedule.Detail.Task.Category'),
        accessor: 'categories',
        Cell: ({ row }: { row: { original: ScheduleTask } }) => (
          <span className="text-[13px] font-semibold text-gray-900">
            {row.original.categories.length > 0
              ? row.original.categories.join(', ')
              : t('Schedule.Detail.Task.NoCategory')}
          </span>
        ),
      },
      {
        Header: t('Schedule.Detail.Task.TaskType'),
        accessor: 'task_type_id',
        Cell: ({ row }: { row: { original: ScheduleTask } }) => (
          <div className="flex items-center gap-2">
            <span>{getTaskTypeLabel(row.original.task_type_id)}</span>
            {mayNotFitTaskIds.has(row.original.id) && (
              <span className="whitespace-nowrap rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                {t('Schedule.Detail.Task.MayNotFit')}
              </span>
            )}
            {notContractedTaskIds.has(row.original.id) && (
              <span
                title={t('Schedule.Detail.Task.NotContractedHint')}
                className="whitespace-nowrap rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500">
                {t('Schedule.Detail.Task.NotContracted')}
              </span>
            )}
          </div>
        ),
      },
      {
        Header: t('Schedule.Detail.Task.ReviewStatus'),
        accessor: 'review_status',
        width: 'w-40',
        Cell: ({ row }: { row: { original: ScheduleTask } }) => {
          const task = row.original;
          const currentFilter = getReviewStatusFilter(task.review_status);

          if (!currentFilter) {
            return (
              <Badge
                variant="gray"
                label={t('Schedule.Detail.ReviewStatusPublished')}
                className={REVIEW_STATUS_CHIP_SIZE_CLASS_NAME}
              />
            );
          }

          if (updatingTaskIds.has(task.id)) {
            return <Loader2 size={16} className="animate-spin text-gray-400" />;
          }

          if (!canManage) {
            return (
              <Badge
                variant={REVIEW_STATUS_FILTER_VARIANT[currentFilter]}
                label={t(`Schedule.Detail.ReviewStatus.${currentFilter}`)}
                showDot
                className={cn(REVIEW_STATUS_CHIP_SIZE_CLASS_NAME, REVIEW_STATUS_TRIGGER_CLASS_NAME[currentFilter])}
              />
            );
          }

          return (
            <Select
              value={currentFilter}
              onValueChange={(next) => handleReviewStatusChange(task, next as ReviewStatusFilter)}>
              <SelectTrigger
                aria-label={t('Schedule.Detail.Task.ReviewStatusAriaLabel', {
                  taskType: getTaskTypeLabel(task.task_type_id),
                })}
                className={cn(
                  'w-28 gap-1.5',
                  REVIEW_STATUS_CHIP_SIZE_CLASS_NAME,
                  REVIEW_STATUS_TRIGGER_CLASS_NAME[currentFilter],
                )}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reviewStatusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} className="text-xs">
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          );
        },
      },
      {
        accessor: 'id',
        width: 'w-10',
        Cell: ({ row }: { row: { original: ScheduleTask } }) => {
          const task = row.original;
          if (!canManage) return null;
          if (removingTaskId === task.id) {
            return <Loader2 size={16} className="animate-spin text-gray-400" />;
          }
          return (
            <button
              type="button"
              aria-label={t('Schedule.Detail.Task.RemoveAriaLabel', {
                taskType: getTaskTypeLabel(task.task_type_id),
              })}
              disabled={removingTaskId !== null}
              onClick={() => setTaskToRemove(task)}
              className="flex h-6 w-6 items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-700 disabled:cursor-not-allowed">
              <X size={14} />
            </button>
          );
        },
      },
    ],
    [
      t,
      taskTypesById,
      getTaskTypeLabel,
      canManage,
      reviewStatusOptions,
      updatingTaskIds,
      reorderingTaskId,
      removingTaskId,
      positionById,
      mayNotFitTaskIds,
      notContractedTaskIds,
    ],
  );

  if (tasks.length === 0) {
    return (
      <div className={TABLE_SURFACE_CLASS_NAME}>
        <p className="py-10 text-center text-sm text-gray-500">{t('Schedule.Detail.NoTasks')}</p>
      </div>
    );
  }

  return (
    <>
      <div className={TABLE_SURFACE_CLASS_NAME}>
        <Table
          columns={columns}
          data={sortedTasks}
          pageSize={10}
          pagination={sortedTasks.length > 10}
          headerCellClassName={HEADER_CELL_CLASS_NAME}
          cellClassName={CELL_CLASS_NAME}
          getRowProps={(row: { original: ScheduleTask }) => {
            const task = row.original;
            const isDragged = draggedTaskId === task.id;
            const isDropTarget = dropTargetTaskId === task.id && !isDragged;
            const mayNotFit = mayNotFitTaskIds.has(task.id);
            const isExcluded = task.review_status === 'rejected' || notContractedTaskIds.has(task.id);
            return {
              onDragOver: (event: DragEvent) => {
                if (!draggedTaskId) return;
                event.preventDefault();
                if (dropTargetTaskId !== task.id) setDropTargetTaskId(task.id);
              },
              onDragLeave: () => {
                if (dropTargetTaskId === task.id) setDropTargetTaskId(null);
              },
              onDrop: (event: DragEvent) => {
                event.preventDefault();
                handleDrop(task).catch(() => {});
              },
              className: cn(
                'transition-colors hover:bg-gray-50',
                isDropTarget ? 'bg-brand-50' : (mayNotFit || isExcluded) && 'bg-gray-50',
                isDragged ? 'opacity-40' : mayNotFit ? 'opacity-[0.66]' : isExcluded && 'opacity-[0.62]',
              ),
            };
          }}
        />
      </div>
      <ConfirmationModal
        isOpen={taskToRemove !== null}
        onClose={() => setTaskToRemove(null)}
        onConfirm={handleConfirmRemove}
        confirmText={taskToRemove ? getRemoveConfirmText(taskToRemove) : ''}
        buttonNamePrefix="SCHEDULE_DETAIL_REMOVE_TASK"
      />
    </>
  );
};

export default TaskTable;
